import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, addDoc, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../Services/firebase-config';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import ApplicationCard from './ApplicationCard';
import ErrorModal from './ErrorModal';
import SuccessModal from './SuccessModal';
import { Search, Filter, Users, Trophy, TrendingUp } from 'lucide-react';
import { preloadNextPageImages } from './imagePreloader';
const VotingPage = () => {
    const [applications, setApplications] = useState([]);
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [visitorId, setVisitorId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [categories, setCategories] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [applicationsPerPage] = useState(12); // Show 12 applications per page
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [stats, setStats] = useState({
        totalApplications: 0,
        totalVotes: 0,
        topApplication: null,
        categoryStats: []
    });
    const [canVote, setCanVote] = useState(false);
    // Error modal state
    const [errorModal, setErrorModal] = useState({
        isOpen: false,
        title: '',
        message: ''
    });
    // Success modal state
    const [successModal, setSuccessModal] = useState({
        isOpen: false,
        title: '',
        message: ''
    });

    // Helper function to show error modal
    const showError = (title, message) => {
        setErrorModal({
            isOpen: true,
            title,
            message
        });
    };

    // Helper function to close error modal
    const closeErrorModal = () => {
        setErrorModal({
            isOpen: false,
            title: '',
            message: ''
        });
    };

    // Helper function to show success modal
    const showSuccess = (title, message) => {
        setSuccessModal({
            isOpen: true,
            title,
            message
        });
    };

    // Helper function to close success modal
    const closeSuccessModal = () => {
        setSuccessModal({
            isOpen: false,
            title: '',
            message: ''
        });
    };

    // Initialize FingerprintJS
    useEffect(() => {
        const initFingerprint = async () => {
            try {
                const fp = await FingerprintJS.load();
                const result = await fp.get();
                setVisitorId(result.visitorId);
                console.log('Visitor ID:', result.visitorId);
            } catch (error) {
                console.error('Error initializing FingerprintJS:', error);
            }
        };

        initFingerprint();
    }, []);

    // Filter applications based on search and category
    useEffect(() => {
        let filtered = applications;

        if (searchTerm) {
            filtered = filtered.filter(app =>
                app.institution.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.exhibitorNames.join(', ').toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.innovationDescription.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (selectedCategory !== 'all') {
            filtered = filtered.filter(app => app.category === selectedCategory);
        }

        // Ensure filtered results are also sorted by vote count (highest first)
        filtered.sort((a, b) => (b.votes || 0) - (a.votes || 0));

        setFilteredApplications(filtered);
        setCurrentPage(1); // Reset to first page when filters change
    }, [applications, searchTerm, selectedCategory]);

    // Calculate pagination
    const totalPages = Math.ceil(filteredApplications.length / applicationsPerPage);
    const indexOfLastApp = currentPage * applicationsPerPage;
    const indexOfFirstApp = indexOfLastApp - applicationsPerPage;
    const currentApplications = filteredApplications.slice(indexOfFirstApp, indexOfLastApp);

    const handlePageChange = (pageNumber) => {
        setIsPageLoading(true);
        setCurrentPage(pageNumber);
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Reset loading state after a brief delay to show transition
        setTimeout(() => setIsPageLoading(false), 300);
    };

    // Preload images for the next page when current page changes
    useEffect(() => {
        if (filteredApplications.length > 0 && currentPage < totalPages) {
            // Preload next page images with a small delay to avoid blocking current page render
            const timeoutId = setTimeout(() => {
                preloadNextPageImages(filteredApplications, currentPage, applicationsPerPage);
            }, 500);

            return () => clearTimeout(timeoutId);
        }
    }, [currentPage, filteredApplications, totalPages, applicationsPerPage]);

    // Real-time listener for vote counts from Firebase
    const setupVoteListener = useCallback(() => {
        try {
            const votesRef = collection(db, 'votes');

            // Set up real-time listener
            const unsubscribe = onSnapshot(votesRef, (querySnapshot) => {
                const counts = {};

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    const applicationId = data.applicationId;
                    if (counts[applicationId]) {
                        counts[applicationId]++;
                    } else {
                        counts[applicationId] = 1;
                    }
                });

                // Update applications with new vote counts
                setApplications(prevApps => {
                    const updatedApps = prevApps.map(app => ({
                        ...app,
                        votes: counts[app.id] || 0
                    }));

                    // Sort applications by vote count (highest first)
                    updatedApps.sort((a, b) => (b.votes || 0) - (a.votes || 0));

                    // Calculate stats
                    let totalVotes = 0;
                    let topApp = null;
                    let maxVotes = 0;
                    const categoryVotes = {};

                    updatedApps.forEach((app) => {
                        const voteCount = app.votes;
                        totalVotes += voteCount;

                        // Track top application
                        if (voteCount > maxVotes) {
                            maxVotes = voteCount;
                            topApp = app;
                        }

                        // Calculate category votes
                        const category = app.category || 'Uncategorized';
                        if (categoryVotes[category]) {
                            categoryVotes[category] += voteCount;
                        } else {
                            categoryVotes[category] = voteCount;
                        }
                    });

                    // Convert category votes to array and sort by votes
                    const categoryStats = Object.entries(categoryVotes)
                        .map(([category, votes]) => ({ category, votes }))
                        .sort((a, b) => b.votes - a.votes);

                    // Update stats
                    setStats({
                        totalApplications: updatedApps.length,
                        totalVotes,
                        topApplication: topApp,
                        categoryStats
                    });

                    return updatedApps;
                });
            }, (error) => {
                console.error('Error listening to vote updates:', error);
            });

            return unsubscribe;
        } catch (error) {
            console.error('Error setting up vote listener:', error);
            return null;
        }
    }, []);

    // Fetch voting configuration from Firebase
    const fetchVotingConfig = useCallback(async () => {
        try {
            const configRef = doc(db, 'configs', 'voting');
            const configSnapshot = await getDoc(configRef);

            if (configSnapshot.exists()) {
                const data = configSnapshot.data();
                setCanVote(data.canVote || false);
            } else {
                setCanVote(false);
            }
        } catch (error) {
            console.error('Error fetching voting config:', error);
            setCanVote(false);
        }
    }, []);

    // Set up real-time listener for voting configuration
    const setupVotingConfigListener = useCallback(() => {
        try {
            const configRef = doc(db, 'configs', 'voting');

            // Set up real-time listener
            const unsubscribe = onSnapshot(configRef, (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    setCanVote(data.canVote || false);
                } else {
                    setCanVote(false);
                }
            }, (error) => {
                console.error('Error listening to voting config updates:', error);
            });

            return unsubscribe;
        } catch (error) {
            console.error('Error setting up voting config listener:', error);
            return null;
        }
    }, []);

    // Fetch vote counts from Firebase (for initial load)
    const fetchVoteCounts = async () => {
        try {
            const votesRef = collection(db, 'votes');
            const querySnapshot = await getDocs(votesRef);
            const counts = {};

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                const applicationId = data.applicationId;
                if (counts[applicationId]) {
                    counts[applicationId]++;
                } else {
                    counts[applicationId] = 1;
                }
            });

            return counts;
        } catch (error) {
            console.error('Error fetching vote counts:', error);
            return {};
        }
    };

    const loadApplications = useCallback(async () => {
        try {
            setLoading(true);

            // Fetch projects from Firebase
            const projectsRef = collection(db, 'projects');
            const projectsSnapshot = await getDocs(projectsRef);

            if (projectsSnapshot.empty) {
                setError('No projects found.');
                return;
            }

            // Convert Firestore documents to application objects
            const apps = projectsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Fetch initial vote counts from Firebase
            const counts = await fetchVoteCounts();

            const categorySet = new Set();
            let totalVotes = 0;
            let topApp = null;
            let maxVotes = 0;
            const categoryVotes = {};

            // Add vote counts to applications and calculate stats
            apps.forEach((app) => {
                const voteCount = counts[app.id] || 0;
                app.votes = voteCount; // Add votes from Firebase

                categorySet.add(app.category);
                totalVotes += voteCount;

                // Track top application
                if (voteCount > maxVotes) {
                    maxVotes = voteCount;
                    topApp = app;
                }

                // Calculate category votes
                const category = app.category || 'Uncategorized';
                if (categoryVotes[category]) {
                    categoryVotes[category] += voteCount;
                } else {
                    categoryVotes[category] = voteCount;
                }
            });

            // Convert category votes to array and sort by votes
            const categoryStats = Object.entries(categoryVotes)
                .map(([category, votes]) => ({ category, votes }))
                .sort((a, b) => b.votes - a.votes);

            // Sort applications by vote count (highest first)
            apps.sort((a, b) => (b.votes || 0) - (a.votes || 0));

            setApplications(apps);
            setFilteredApplications(apps);
            setCategories(Array.from(categorySet));
            setStats({
                totalApplications: apps.length,
                totalVotes,
                topApplication: topApp,
                categoryStats
            });
        } catch (error) {
            console.error('Error loading applications:', error);
            setError('Failed to load applications. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Set up real-time vote listener
    useEffect(() => {
        let voteUnsubscribe = null;
        let configUnsubscribe = null;

        const initializeListener = async () => {
            // First load the applications and voting config
            await loadApplications();
            await fetchVotingConfig();

            // Then set up the real-time listeners
            voteUnsubscribe = setupVoteListener();
            configUnsubscribe = setupVotingConfigListener();
        };

        initializeListener();

        // Cleanup listeners on unmount
        return () => {
            if (voteUnsubscribe) {
                voteUnsubscribe();
            }
            if (configUnsubscribe) {
                configUnsubscribe();
            }
        };
    }, [loadApplications, setupVoteListener, fetchVotingConfig, setupVotingConfigListener]);

    const handleVote = async (applicationId, voterEmail) => {
        // Check if voting is enabled
        if (!canVote) {
            showError(
                'Voting Has Ended',
                'Thank you for your interest! The voting period for this event has officially ended. The results will be announced soon. Stay tuned for updates!'
            );
            return;
        }

        if (!visitorId) {
            showError('Unable to Process Vote', 'Unable to process vote. Please refresh the page and try again.');
            return;
        }

        if (!voterEmail || !voterEmail.includes('@')) {
            showError('Invalid Email', 'Please enter a valid email address to vote.');
            return;
        }

        try {
            // Check if user has already voted for ANY application (not just this one)
            const votesRef = collection(db, 'votes');

            // Check by visitor ID (device fingerprint) - for any application
            const visitorVoteQuery = query(
                votesRef,
                where('visitorId', '==', visitorId)
            );

            const existingVisitorVoteSnapshot = await getDocs(visitorVoteQuery);

            if (!existingVisitorVoteSnapshot.empty) {
                showError('Vote Already Submitted', 'You have already voted in this Project! Each person can only vote once across all applications.');
                return;
            }

            // Also check by email - for any application
            const emailVoteQuery = query(
                votesRef,
                where('voterEmail', '==', voterEmail.toLowerCase())
            );

            const existingEmailVoteSnapshot = await getDocs(emailVoteQuery);

            if (!existingEmailVoteSnapshot.empty) {
                showError('Email Already Used', 'This email address has already been used to vote in this Project! Each email can only be used once across all applications.');
                return;
            }

            // Record the vote
            await addDoc(votesRef, {
                applicationId,
                visitorId,
                voterEmail: voterEmail.toLowerCase(),
                timestamp: new Date(),
                ipAddress: null // Could be added if needed
            });

            showSuccess('Vote Submitted!', 'Vote submitted successfully! Thank you for participating.');

            // No need to manually refresh - the real-time listener will handle updates
        } catch (error) {
            console.error('Error submitting vote:', error);
            showError('Vote Submission Failed', 'Failed to submit vote. Please try again.');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading applications...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 mb-4">{error}</p>
                    <button
                        onClick={loadApplications}
                        className="px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/90"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b pt-20 sm:pt-16 pb-6 lg:pb-0">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Project Voting</h1>
                            <p className="text-gray-600 mt-1">Vote for your favorite Project applications</p>
                        </div>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-4">
                            <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
                                <Users className="w-5 h-5 text-blue-600" />
                                <span className="text-sm font-medium text-blue-900">
                                    {stats.totalApplications} Applications
                                </span>
                            </div>
                            <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg">
                                <TrendingUp className="w-5 h-5 text-green-600" />
                                <span className="text-sm font-medium text-green-900">
                                    {stats.totalVotes} Total Votes
                                </span>
                            </div>
                            {stats.topApplication && stats.topApplication.votes > 0 && (
                                <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-50 to-orange-50 px-4 py-2 rounded-lg border border-yellow-200">
                                    <Trophy className="w-5 h-5 text-yellow-600" />
                                    <span className="text-sm font-medium text-yellow-900">
                                        🏆 Leading: {stats.topApplication.institution} ({stats.topApplication.votes} votes)
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                    {/* Search */}
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search applications by team name, leader, or category..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-blue-1 focus:border-transparent"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Category Filter */}
                    <div className="relative">
                        <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <select
                            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-blue-1 focus:border-transparent bg-white min-w-[200px]"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(category => (
                                <option key={category} value={category}>
                                    {category}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Leading Project Highlight */}
                {stats.topApplication && stats.topApplication.votes > 0 && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 via-orange-50 to-yellow-50 border-2 border-yellow-200 rounded-xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="bg-yellow-400 p-2 rounded-full">
                                    <Trophy className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-yellow-900">🏆 Currently Leading</h3>
                                    <p className="text-sm text-yellow-800">
                                        <span className="font-semibold">{stats.topApplication.institution}</span> by {Array.isArray(stats.topApplication.exhibitorNames) ? stats.topApplication.exhibitorNames.join(', ') : stats.topApplication.exhibitorNames}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="bg-yellow-100 px-4 py-2 rounded-lg border border-yellow-300">
                                    <p className="text-2xl font-bold text-yellow-900">{stats.topApplication.votes}</p>
                                    <p className="text-xs text-yellow-700 font-medium">VOTES</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Applications Grid */}
                {filteredApplications.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">No applications found matching your criteria.</p>
                    </div>
                ) : (
                    <>
                        {/* Results Summary */}
                        <div className="mb-4 text-sm text-gray-600">
                            Showing {indexOfFirstApp + 1} - {Math.min(indexOfLastApp, filteredApplications.length)} of {filteredApplications.length} applications
                            {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                        </div>

                        {/* Applications Grid */}
                        <div className="relative">
                            {/* Page loading overlay */}
                            {isPageLoading && (
                                <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center">
                                    <div className="flex items-center space-x-2">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-custom-blue-1"></div>
                                        <span className="text-gray-600">Loading applications...</span>
                                    </div>
                                </div>
                            )}

                            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8 transition-opacity duration-300 ${isPageLoading ? 'opacity-50' : 'opacity-100'
                                }`}>
                                {currentApplications.map((application) => (
                                    <ApplicationCard
                                        key={application.id}
                                        application={application}
                                        onVote={handleVote}
                                        visitorId={visitorId}
                                        isLeading={stats.topApplication && stats.topApplication.id === application.id && stats.topApplication.votes > 0}
                                        canVote={canVote}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center space-x-2 mt-8">
                                {/* Previous Button */}
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>

                                {/* Page Numbers */}
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
                                    // Show first page, last page, current page, and pages around current
                                    const showPage = pageNumber === 1 ||
                                        pageNumber === totalPages ||
                                        Math.abs(pageNumber - currentPage) <= 1;

                                    if (!showPage) {
                                        // Show ellipsis for gaps
                                        if (pageNumber === 2 && currentPage > 4) {
                                            return <span key={pageNumber} className="px-2 text-gray-400">...</span>;
                                        }
                                        if (pageNumber === totalPages - 1 && currentPage < totalPages - 3) {
                                            return <span key={pageNumber} className="px-2 text-gray-400">...</span>;
                                        }
                                        return null;
                                    }

                                    return (
                                        <button
                                            key={pageNumber}
                                            onClick={() => handlePageChange(pageNumber)}
                                            className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === pageNumber
                                                ? 'bg-custom-blue-1 text-white'
                                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            {pageNumber}
                                        </button>
                                    );
                                })}

                                {/* Next Button */}
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Error Modal */}
            <ErrorModal
                isOpen={errorModal.isOpen}
                onClose={closeErrorModal}
                title={errorModal.title}
                message={errorModal.message}
            />

            {/* Success Modal */}
            <SuccessModal
                isOpen={successModal.isOpen}
                onClose={closeSuccessModal}
                title={successModal.title}
                message={successModal.message}
            />
        </div>
    );
};

export default VotingPage;